//product-form.js
import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

/* ================= CONSTANTS ================= */
export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;
const ERROR_BUTTON_REENABLE_DELAY = 1000;
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];
  #animationTimeout;
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearTimeouts();
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  #clearTimeouts() {
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const quantitySelector = form.querySelector('quantity-selector-component');
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) return;
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';
    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;
    if (image) preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart() {
    const { addToCartButton } = this.refs;
    this.#clearTimeouts();

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #timeout;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = this.querySelector('input[name="id"]');
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const cartItem = cart.items.find(item => item.variant_id.toString() === variantIdInput.value.toString());
      const cartQty = cartItem?.quantity || 0;

      const quantitySelector = this.querySelector('quantity-selector-component');
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      this.#updateQuantityLabel(cartQty);
      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  #onCartUpdate = async (event) => {
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;
    await this.#fetchAndUpdateCartQuantity();
  };

  handleSubmit(event) {
    event.preventDefault();

    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    if (this.#timeout) clearTimeout(this.#timeout);
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    // Validate quantity constraints
    const quantitySelector = this.querySelector('quantity-selector-component');
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        this.#handleQuantityError(validation, addToCartTextError, addToCartButtonContainer);
        return;
      }
    }

    this.#submitToCart(form, addToCartTextError, event);
  }

  #handleQuantityError(validation, addToCartTextError, addToCartButtonContainer) {
    addToCartButtonContainer?.disable();

    const errorTemplate = this.dataset.quantityErrorMax || '';
    const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
    
    if (addToCartTextError) {
      this.#showError(addToCartTextError, errorMessage);
      
      this.#timeout = setTimeout(() => {
        addToCartTextError.classList.add('hidden');
        this.#clearLiveRegionText();
      }, ERROR_MESSAGE_DISPLAY_DURATION);
    }

    setTimeout(() => {
      addToCartButtonContainer?.enable();
    }, ERROR_BUTTON_REENABLE_DELAY);
  }

  #submitToCart(form, addToCartTextError, event) {
    const formData = new FormData(form);

    // Add section IDs for cart items components
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = Array.from(cartItemsComponents)
      .filter(item => item instanceof HTMLElement && item.dataset.sectionId)
      .map(item => item.dataset.sectionId);
    
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then(response => response.json())
      .then(response => {
        if (response.status) {
          this.#handleCartError(response, formData, addToCartTextError, form);
        } else {
          this.#handleCartSuccess(response, formData, addToCartTextError);
        }
      })
      .catch(error => console.error(error))
      .finally(() => cartPerformance.measureFromEvent('add:user-action', event));
  }

  #handleCartError(response, formData, addToCartTextError, form) {
    this.dispatchEvent(
      new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
    );

    if (addToCartTextError) {
      this.#showError(addToCartTextError, response.message);
      
      this.#timeout = setTimeout(() => {
        addToCartTextError.classList.add('hidden');
        this.#clearLiveRegionText();
      }, ERROR_MESSAGE_DISPLAY_DURATION);
    }

    this.dispatchEvent(
      new CartAddEvent({}, this.id, {
        didError: true,
        source: 'product-form-component',
        itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
        productId: this.dataset.productId,
      })
    );
  }

  #handleCartSuccess(response, formData, addToCartTextError) {
    const id = formData.get('id');
    if (!id) throw new Error('Form ID is required');

    if (addToCartTextError) {
      addToCartTextError.classList.add('hidden');
      addToCartTextError.removeAttribute('aria-live');
    }

    // Announce success to screen readers
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
      const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
      const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
      const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

      this.#setLiveRegionText(addedText);
      setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
    }

    this.#fetchAndUpdateCartQuantity();

    this.dispatchEvent(
      new CartAddEvent({}, id.toString(), {
        source: 'product-form-component',
        itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
        productId: this.dataset.productId,
        sections: response.sections,
      })
    );
  }

  #showError(errorElement, message) {
    errorElement.classList.remove('hidden');
    const textNode = errorElement.childNodes[2];
    
    if (textNode) {
      textNode.textContent = message;
    } else {
      errorElement.appendChild(document.createTextNode(message));
    }
    
    this.#setLiveRegionText(message);
  }

  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (!quantityLabel) return;

    const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
    if (inCartText?.[2]) {
      quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
    }

    quantityLabel.classList.toggle('hidden', cartQty === 0);
  }

  #setLiveRegionText(text) {
    this.refs.liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    this.refs.liveRegion.textContent = '';
  }

  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', newElement.cloneNode(true));
    }
  }

  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;
    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update button state and text
    if (currentAddToCartButton) {
      const isAvailable = event.detail.resource && event.detail.resource.available;
      
      if (isAvailable) {
        addToCartButtonContainer.enable();
      } else {
        addToCartButtonContainer.disable();
      }

      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    // Update accelerated checkout button
    if (this.refs.acceleratedCheckoutButtonContainer) {
      const isAvailable = event.detail.resource && event.detail.resource.available;
      
      if (isAvailable) {
        this.refs.acceleratedCheckoutButtonContainer.removeAttribute('hidden');
      } else {
        this.refs.acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
      }
    }

    // Update product variant media
    if (event.detail.resource?.featured_media?.preview_image?.src) {
      const productVariantMedia = event.detail.resource.featured_media.preview_image.src;
      addToCartButtonContainer?.setAttribute('data-product-variant-media', `${productVariantMedia}&width=100`);
    }

    // Update quantity selector constraints
    const quantitySelector = this.querySelector('quantity-selector-component');
    const newQuantityInput = event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]');

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    // Handle quantity rules changes
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      await this.#handleQuantityRulesChange(event, quantitySelector);
    } else {
      this.#updateIndividualElements(event, quantitySelector, quantityRules, newQuantityRules);
    }

    await this.#fetchAndUpdateCartQuantity();
  };

  async #handleQuantityRulesChange(event, quantitySelector) {
    const currentQuantityValue = quantitySelector.getValue?.();
    const currentProductFormButtons = this.refs.productFormButtons;
    const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

    if (currentProductFormButtons && newProductFormButtons) {
      morph(currentProductFormButtons, newProductFormButtons);

      const newQuantitySelector = this.querySelector('quantity-selector-component');
      const newQuantityInputElement = event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]');

      if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
        newQuantitySelector.setValue(currentQuantityValue);
        newQuantitySelector.updateConstraints(
          newQuantityInputElement.min,
          newQuantityInputElement.max || null,
          newQuantityInputElement.step
        );
      }
    }
  }

  #updateIndividualElements(event, quantitySelector, quantityRules, newQuantityRules) {
    const quantityLabel = this.querySelector('.quantity-label');
    const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
    this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

    const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
    this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
  }

  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

/**
 * Fly-to-cart animation element
 */
class FlyToCart extends HTMLElement {
  source;
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };

    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 };
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 };

    let startTime = null;
    const duration = 600;

    this.style.opacity = '1';
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const position = this.#bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);
      this.style.setProperty('--scale', `${1 - progress * 0.5}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    requestAnimationFrame(animate);
  }

  #bezierPoint(t, p0, p1, p2, p3) {
    const cX = 3 * (p1.x - p0.x);
    const bX = 3 * (p2.x - p1.x) - cX;
    const aX = p3.x - p0.x - cX - bX;

    const cY = 3 * (p1.y - p0.y);
    const bY = 3 * (p2.y - p1.y) - cY;
    const aY = p3.y - p0.y - cY - bY;

    const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
    const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

    return { x, y };
  }
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

/* ================= ULTRA-FAST REWARD SYSTEM ================= */
document.addEventListener('DOMContentLoaded', () => {
  const config = {
    freeShipping: Number(window.themeSettings.free_shipping) * 100,
    giftThreshold: Number(window.themeSettings.free_surprize_gift) * 100,
    productThreshold: Number(window.themeSettings.free_product) * 100,
    giftVariant: Number(window.themeSettings.free_surprise_gift_variant),
    productVariant: Number(window.themeSettings.free_magnesium_variant),
    giftTitle: window.themeSettings.free_surprise_gift_title,
    productTitle: window.themeSettings.free_magnesium_title,
    giftImage: window.themeSettings.free_surprise_gift_image,
    productImage: window.themeSettings.free_magnesium_image,
  };

  const state = {
    isProcessing: false,
    previousTotal: 0,
    hasShownConfetti: false,
    lastCartState: null,
    productData: {
      gift: null,
      product: null,
    },
  };

  /* ================= OPTIMIZED CART API ================= */
  const cartAPI = {
    async get() {
      const response = await fetch('/cart.js');
      return response.json();
    },

    // Batch update - single API call to update multiple items
    async batchUpdate(updates) {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      return response.json();
    },

    async addItem(id) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: String(id),
          quantity: 1,
          properties: { _reward: 'true' },
        }),
      });
      return response.json();
    },

    async removeItem(id) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: String(id),
          quantity: 0,
        }),
      });
      return response.json();
    },

    async getProductByVariant(variantId) {
      try {
        const response = await fetch(`/products.json?limit=250`);
        const data = await response.json();
        
        for (const product of data.products) {
          const variant = product.variants.find(v => v.id === variantId);
          if (variant) {
            return {
              title: product.title,
              image: product.images[0]?.src || '',
              price: variant.price,
              compareAtPrice: variant.compare_at_price,
            };
          }
        }
        return null;
      } catch (error) {
        console.error('Error fetching product:', error);
        return null;
      }
    },

    refresh() {
      document.dispatchEvent(
        new CustomEvent(ThemeEvents.cartUpdate, {
          detail: {
            sourceId: 'reward-system',
            data: { source: 'reward-system' },
          },
        })
      );
    },
  };

  /* ================= UI HELPERS ================= */
  const ui = {
    triggerConfetti() {
      if (typeof confetti !== 'undefined') {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
      }
    },

    async fetchProductData() {
      if (!state.productData.gift && config.giftVariant) {
        state.productData.gift = await cartAPI.getProductByVariant(config.giftVariant);
      }
      if (!state.productData.product && config.productVariant) {
        state.productData.product = await cartAPI.getProductByVariant(config.productVariant);
      }
    },

    initializeCheckpoints() {
      const container = document.querySelector('#reward-bar-container');
      if (!container) return;

      const existingCheckpoints = container.querySelectorAll('.reward-checkpoint');
      if (existingCheckpoints.length === 3) return;

      existingCheckpoints.forEach(cp => cp.remove());

      const checkpoints = [
        { 
          threshold: config.freeShipping, 
          position: (config.freeShipping / config.productThreshold) * 100 
        },
        { 
          threshold: config.giftThreshold, 
          position: (config.giftThreshold / config.productThreshold) * 100 
        },
        { 
          threshold: config.productThreshold, 
          position: 100 
        }
      ];

      checkpoints.forEach((checkpoint) => {
        const checkpointEl = document.createElement('div');
        checkpointEl.className = 'reward-checkpoint';
        checkpointEl.style.left = `${checkpoint.position}%`;
        checkpointEl.dataset.threshold = checkpoint.threshold;
        
        container.appendChild(checkpointEl);
      });
    },

    updateCheckpoints(total) {
      const checkpoints = document.querySelectorAll('.reward-checkpoint');
      
      checkpoints.forEach((checkpoint) => {
        const threshold = Number(checkpoint.dataset.threshold);
        checkpoint.classList.remove('active', 'completed');
        
        if (total >= threshold) {
          checkpoint.classList.add('completed');
        }
      });
    },

    createProductBadge(product, isGift) {
      if (!product) return '';
      
      return `
        <span class="reward-product-badge">
          <img src="${product.image}" alt="${product.title}" class="reward-product-image" />
          <span class="reward-product-info">
            <span>${product.title}</span>
          </span>
        </span>
      `;
    },

    getRewardText(total) {
      const shipping = config.freeShipping - total;
      const gift = config.giftThreshold - total;
      const product = config.productThreshold - total;

      if (total >= config.productThreshold) {
        return {
          mainText: '',
          subText: 'CONGRATS! YOU\'VE GOT TWO FREE PRODUCTS',
          isSuccess: true
        };
      }

      if (total >= config.giftThreshold) {
        const productBadge = this.createProductBadge(state.productData.product, false);
        const displayText = productBadge || (config.productTitle && config.productImage ? `
          <span class="reward-product-badge">
            <img src="${config.productImage}" alt="${config.productTitle}" class="reward-product-image" />
            <span class="reward-product-info">
              <span>${config.productTitle}</span>
            </span>
          </span>
        ` : config.productTitle || '');
        return {
          mainText: `$${(product / 100).toFixed(0)} AWAY FROM ${displayText}`,
          subText: 'CONGRATS! YOU\'VE GOT ONE FREE PRODUCT',
          isSuccess: false
        };
      }

      if (total >= config.freeShipping) {
        const giftBadge = this.createProductBadge(state.productData.gift, true);
        const displayText = giftBadge || (config.giftTitle && config.giftImage ? `
          <span class="reward-product-badge">
            <img src="${config.giftImage}" alt="${config.giftTitle}" class="reward-product-image" />
            <span class="reward-product-info">
              <span>${config.giftTitle}</span>
            </span>
          </span>
        ` : config.giftTitle || '');
        return {
          mainText: `$${(gift / 100).toFixed(0)} AWAY FROM ${displayText}`,
          subText: '',
          isSuccess: false
        };
      }

      return {
        mainText: `$${(shipping / 100).toFixed(0)} AWAY FROM FREE SHIPPING`,
        subText: '',
        isSuccess: false
      };
    },

    async renderProgress(total) {
      await this.fetchProductData();
      this.initializeCheckpoints();
      
      const bar = document.querySelector('#reward-bar span');
      const mainText = document.querySelector('#reward-text');
      const subText = document.querySelector('#reward-message');
      if (!bar || !mainText || !subText) return;

      const percent = Math.min((total / config.productThreshold) * 100, 100);
      bar.style.width = `${percent}%`;

      const rewardText = this.getRewardText(total);
      mainText.innerHTML = rewardText.mainText;
      subText.textContent = rewardText.subText;
      
      if (rewardText.isSuccess) {
        mainText.classList.add('success');
        subText.classList.add('success');
      } else {
        mainText.classList.remove('success');
        subText.classList.remove('success');
      }

      this.updateCheckpoints(total);
    },
  };

  /* ================= CHECKOUT BUTTON CONTROL ================= */
  const checkoutButton = {
    disable() {
      const button = document.querySelector('#checkout');
      if (button) {
        button.disabled = true;
        button.classList.add('processing');
      }
    },
    
    enable() {
      const button = document.querySelector('#checkout');
      if (button) {
        setTimeout(() => {
          const cart = document.querySelector('cart-drawer, .cart-drawer');
          const isEmpty = cart?.querySelector('.cart__empty');
          
          if (!isEmpty) {
            button.disabled = false;
            button.classList.remove('processing');
          }
        }, 50);
      }
    }
  };

  /* ================= ULTRA-FAST REWARD ENGINE ================= */
  
  // Calculate what rewards should be present
  function calculateDesiredRewards(realTotal) {
    return {
      shouldHaveGift: realTotal > config.giftThreshold,
      shouldHaveProduct: realTotal > config.productThreshold,
    };
  }

  // Single optimized function using batch update
  async function syncRewards(cart) {
    const hasGift = cart.items.some((i) => i.variant_id == config.giftVariant);
    const hasProduct = cart.items.some((i) => i.variant_id == config.productVariant);

    const realTotal = cart.items
      .filter((i) => i.variant_id != config.giftVariant && i.variant_id != config.productVariant)
      .reduce((sum, i) => sum + i.line_price, 0);

    const { shouldHaveGift, shouldHaveProduct } = calculateDesiredRewards(realTotal);

    // Build batch updates object
    const updates = {};
    let needsUpdate = false;

    // Handle gift
    if (shouldHaveGift && !hasGift) {
      // Need to add gift - use add API instead
      await cartAPI.addItem(config.giftVariant);
      needsUpdate = true;
    } else if (!shouldHaveGift && hasGift) {
      updates[config.giftVariant] = 0;
      needsUpdate = true;
    }

    // Handle product
    if (shouldHaveProduct && !hasProduct) {
      // Need to add product - use add API instead
      await cartAPI.addItem(config.productVariant);
      needsUpdate = true;
    } else if (!shouldHaveProduct && hasProduct) {
      updates[config.productVariant] = 0;
      needsUpdate = true;
    }

    // Execute batch removal if needed
    if (Object.keys(updates).length > 0) {
      await cartAPI.batchUpdate(updates);
      needsUpdate = true;
    }

    return { needsUpdate, realTotal };
  }

  async function applyRewards() {
    if (state.isProcessing) {
      console.log('⚡ Reward processing in progress, queuing...');
      return;
    }

    state.isProcessing = true;
    checkoutButton.disable();

    try {
      const cart = await cartAPI.get();
      
      // Quick exit if cart is empty
      if (cart.items.length === 0) {
        ui.renderProgress(0);
        state.previousTotal = 0;
        state.hasShownConfetti = false;
        return;
      }

      const { needsUpdate, realTotal } = await syncRewards(cart);

      // Update UI
      ui.renderProgress(realTotal);

      // Handle confetti
      if (realTotal > config.productThreshold && state.previousTotal <= config.productThreshold && !state.hasShownConfetti) {
        ui.triggerConfetti();
        state.hasShownConfetti = true;
      } else if (realTotal > config.giftThreshold && realTotal <= config.productThreshold && state.previousTotal <= config.giftThreshold && !state.hasShownConfetti) {
        ui.triggerConfetti();
        state.hasShownConfetti = true;
      } else if (realTotal <= config.giftThreshold) {
        state.hasShownConfetti = false;
      }

      state.previousTotal = realTotal;

      if (needsUpdate) {
        console.log('⚡ Refreshing cart UI...');
        cartAPI.refresh();
        setTimeout(() => ui.renderProgress(realTotal), 50);
      }
    } catch (error) {
      console.error('⚡ Error in applyRewards:', error);
    } finally {
      state.isProcessing = false;
      checkoutButton.enable();
    }
  }

  /* ================= INITIALIZATION ================= */
  applyRewards();

  // Ultra-fast event handling with NO debouncing
  let processingQueue = false;
  let queuedUpdate = false;

  async function handleCartEvent(event) {
    if (event.detail?.sourceId === 'reward-system') return;

    // If already processing, queue one more update
    if (state.isProcessing) {
      queuedUpdate = true;
      return;
    }

    checkoutButton.disable();
    
    await applyRewards();

    // Process queued update if any
    if (queuedUpdate) {
      queuedUpdate = false;
      setTimeout(() => applyRewards(), 10);
    }
  }

  document.addEventListener(ThemeEvents.cartUpdate, handleCartEvent);
  document.addEventListener(ThemeEvents.cartAdd, handleCartEvent);

  // Re-render progress bar on DOM changes
  const observer = new MutationObserver(() => {
    const bar = document.querySelector('#reward-bar span');
    const text = document.querySelector('#reward-text');

    if (bar && text && text.textContent.trim() === '') {
      cartAPI.get().then((cart) => {
        const realTotal = cart.items
          .filter((i) => i.variant_id != config.giftVariant && i.variant_id != config.productVariant)
          .reduce((sum, i) => sum + i.line_price, 0);
        ui.renderProgress(realTotal);
      });
    }
  });

  const cartContainer = document.querySelector('cart-drawer, .cart-drawer, #cart-drawer, .drawer__content');
  if (cartContainer) {
    observer.observe(cartContainer, {
      childList: true,
      subtree: true,
    });
  }
});

// Shipping message system
(function() {
  if (window.shippingMessageInitialized) return;
  window.shippingMessageInitialized = true;

  function checkShippingMessage() {
    var msg = document.getElementById('shipping-message');
    if (!msg) {
      console.log('Shipping message element not found');
      return;
    }

    fetch('/cart.js')
      .then((res) => res.json())
      .then((cart) => {
        console.log('Cart total:', cart.total_price);
        
        fetch('/browsing_context_suggestions.json')
          .then((res) => res.json())
          .then((data) => {
            console.log('Country code:', data.detected_values.country.handle);
            
            if (data.detected_values && 
                data.detected_values.country && 
                data.detected_values.country.handle === 'US' &&
                cart.total_price >= 5000) {
              msg.style.display = 'block';
            } else {
              msg.style.display = 'none';
            }
          })
          .catch((error) => console.error('Country fetch error:', error));
      })
      .catch((error) => console.error('Cart fetch error:', error));
  }

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    console.log('Cart update event detected');
    setTimeout(checkShippingMessage, 200);
  });

  document.addEventListener(ThemeEvents.cartAdd, (event) => {
    console.log('Cart add event detected');
    setTimeout(checkShippingMessage, 200);
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.id === 'shipping-message' || (node.querySelector && node.querySelector('#shipping-message'))) {
          console.log('Shipping message added to DOM');
          setTimeout(checkShippingMessage, 100);
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  console.log('Shipping message checker initialized globally');
})();